import type { ProgressStatus } from '../../types'

export type ProgressMap = Record<string, ProgressStatus>

interface CourseRedirectMeta {
    id: string
    firstModuleSlug: string
    moduleSlugs: string[]
    prerequisites: string[]
}

const COURSE_REDIRECT_ORDER: CourseRedirectMeta[] = [
    {
        id: 'terminal',
        firstModuleSlug: 'what-is-terminal',
        prerequisites: [],
        moduleSlugs: [
            'what-is-terminal',
            'basic-navigation',
            'file-manipulation',
            'reading-files',
            'permissions',
            'pipes-redirection',
            'searching-filtering',
            'environment-variables',
            'boss-level-scripting',
        ],
    },
    {
        id: 'git',
        firstModuleSlug: 'what-is-git',
        prerequisites: ['terminal'],
        moduleSlugs: [
            'what-is-git',
            'git-setup',
            'staging-area',
            'first-commit',
            'branching',
            'merging',
            'github',
            'git-workflow',
        ],
    },
    {
        id: 'networking-basics',
        firstModuleSlug: 'what-is-a-network',
        prerequisites: ['terminal'],
        moduleSlugs: [
            'what-is-a-network',
            'ip-addresses',
            'how-packets-work',
            'osi-model',
            'tcp-vs-udp',
            'ports-and-protocols',
            'how-internet-routes',
            'boss-network-debugging',
        ],
    },
    {
        id: 'http',
        firstModuleSlug: 'what-is-http',
        prerequisites: ['networking-basics'],
        moduleSlugs: [
            'what-is-http',
            'request-response-anatomy',
            'http-methods',
            'url-anatomy',
            'status-codes',
            'http-debugging',
            'https-security',
            'http-boss-level',
        ],
    },
    {
        id: 'dns',
        firstModuleSlug: 'what-is-dns',
        prerequisites: ['networking-basics'],
        moduleSlugs: [
            'what-is-dns',
            'domain-names',
            'dns-record-types',
            'dns-resolution',
            'ttl-and-caching',
            'dns-in-practice',
            'dns-security',
            'boss-dns-debugging',
        ],
    },
    {
        id: 'browsers',
        firstModuleSlug: 'what-happens-when-you-hit-enter',
        prerequisites: ['http', 'dns'],
        moduleSlugs: [
            'what-happens-when-you-hit-enter',
            'how-browsers-render-html',
            'javascript-in-the-browser',
            'critical-rendering-path',
            'browser-storage',
            'browser-security-model',
            'devtools',
            'boss-browser-debugging',
        ],
    },
]

function getCoursePath(courseId: string, moduleSlug: string) {
    return `/courses/${courseId}/${moduleSlug}`
}

function isCourseCompleted(courseId: string, progress: ProgressMap) {
    const course = COURSE_REDIRECT_ORDER.find((entry) => entry.id === courseId)
    if (!course) return false
    return course.moduleSlugs.every((slug) => progress[slug] === 'completed')
}

function isCourseUnlocked(course: CourseRedirectMeta, progress: ProgressMap) {
    return course.prerequisites.every((prerequisite) => isCourseCompleted(prerequisite, progress))
}

function getInProgressModulePath(progress: ProgressMap) {
    for (const course of COURSE_REDIRECT_ORDER) {
        const inProgressModule = course.moduleSlugs.find((slug) => progress[slug] === 'in_progress')
        if (inProgressModule) {
            return getCoursePath(course.id, inProgressModule)
        }
    }

    return null
}

export function resolvePostAuthRedirect(progress: ProgressMap) {
    const inProgressPath = getInProgressModulePath(progress)
    if (inProgressPath) return inProgressPath

    const recommendedCourse = COURSE_REDIRECT_ORDER.find(
        (course) => !isCourseCompleted(course.id, progress) && isCourseUnlocked(course, progress),
    )

    if (recommendedCourse) {
        return getCoursePath(recommendedCourse.id, recommendedCourse.firstModuleSlug)
    }

    return '/courses'
}
